import React, { useEffect, useRef } from 'react';
import { useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { batch, connect } from 'react-redux';
import get from 'lodash/get';
import ReactPaginate from 'react-paginate';
import MLIcon from '../../../MLIcon';
import { getConfigObject } from '../../../../utils/config';
import { getGradebook } from '../../../../actions/gradebook';
import { clickPagination } from '../../../../actions/analyticsEvents';
import useSidePanel from '../../../SidePanel/hooks/useSidePanel';
import {
  CLOSE_SIDE_PANEL,
  SET_MEDIA_QUERIES,
} from '../../../SidePanel/store/actions';

import s from './Pagination.module.css';

function Pagination({
  dispatch,
  sendEvent,
  courseId,
  search,
  pageCount,
  page,
}) {
  const { state: sidePanelState, dispatch: sidePanelDispatch } = useSidePanel();
  const {
    mediaQueries: { small },
  } = sidePanelState;

  const isSmall = useMediaQuery(`(max-width: ${small}px)`);
  const paginationRef = useRef(null);
  const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();

  const handlePageClick = ({ selected }) => {
    batch(() => {
      dispatch(
        getGradebook(courseId, {
          page: selected + 1,
          search,
        }),
      );
      dispatch(sendEvent(clickPagination(courseId, selected + 1)));
    });
    if (sidePanelState.open) {
      sidePanelDispatch({ type: CLOSE_SIDE_PANEL });
    }
  };

  useEffect(() => {
    /*
     * Initializes `startingMediaQueries` with base breakpoints for responsive design.
     * Generates `mediaQueries` with 6 total breakpoints, incrementally larger by 44 pixels,
     * creating scalable breakpoints for `medium` and `small` screens.
     * 44 pixels is the static size of each `page` in the pagination component.
     */
    const pageItemSize = 44;
    const courseSideMenu = 70;
    const sidePanelWidth = sidePanelState.open ? 320 : 0;
    const startingMediaQueries = {
      216: {
        medium: 960 + sidePanelWidth + courseSideMenu,
        small: 760 + sidePanelWidth + courseSideMenu,
        mobileOnly: 420 + sidePanelWidth + courseSideMenu,
      },
    };

    const mediaQueries = [...Array(6).keys()].reduce((prev) => {
      const next = {};
      for (const [key, value] of Object.entries(prev)) {
        const updatedKey = parseInt(key) + pageItemSize;
        next[updatedKey] = {
          medium: value.medium + pageItemSize,
          small: value.small + pageItemSize,
          mobileOnly: value.mobileOnly + pageItemSize,
        };
      }
      return { ...prev, ...next };
    }, startingMediaQueries);

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      const matchingMediaQuery = mediaQueries[width];
      if (matchingMediaQuery) {
        sidePanelDispatch({
          type: SET_MEDIA_QUERIES,
          payload: {
            mediaQueries: matchingMediaQuery,
          },
        });
      }
    });

    if (paginationRef.current) {
      resizeObserver.observe(paginationRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [sidePanelState.open, sidePanelDispatch]);

  const initialPage = page > 0 ? page - 1 : page;
  if (FF_SIDE_PANEL_ACTIVE) {
    return (
      <div
        className={classNames(s.paginationContainerDiv, {
          [s.paginationContainerSmall]: isSmall,
        })}
      >
        <div ref={paginationRef}>
          <ReactPaginate
            disableInitialCallback
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePageClick}
            containerClassName={s.paginationContainer}
            pageClassName={s.paginationPage}
            activeClassName={s.paginationPageActive}
            breakClassName={s.paginationBreak}
            previousClassName={s.paginationPreviousButton}
            nextClassName={s.paginationNextButton}
            disabledClassName={s.paginationDisabled}
            forcePage={initialPage}
            data-test-id="pagination"
            previousLabel={
              <MLIcon className={s.paginationLabelIcon} type="chevron_left" />
            }
            nextLabel={
              <MLIcon className={s.paginationLabelIcon} type="chevron_right" />
            }
          />
        </div>
      </div>
    );
  }

  return (
    <ReactPaginate
      disableInitialCallback
      breakLabel={'...'}
      breakClassName={'break-me'}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={5}
      onPageChange={handlePageClick}
      containerClassName={s.pagination}
      activeClassName={s.paginationActive}
      subContainerClassName={'pages pagination'}
      forcePage={initialPage}
      data-test-id="pagination"
      previousLabel={
        <MLIcon className={s.paginationChevron} type="chevron_left" />
      }
      nextLabel={
        <MLIcon className={s.paginationChevron} type="chevron_right" />
      }
    />
  );
}

Pagination.propTypes = {
  dispatch: PropTypes.func.isRequired,
  courseId: PropTypes.string.isRequired,
  sendEvent: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  pageCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
};

function mapStateToProps(state) {
  return {
    search: state.gradebook.searchPhrase,
    courseId: get(state, 'gradebook.courseId', null),
    pageCount: get(state, 'gradebook.pages', 0),
    page: get(state, 'gradebook.page', 0),
  };
}

export default connect(mapStateToProps)(Pagination);
